import { useEffect, useId, useRef, useState } from "react";
import { ClubMark } from "@/components/ClubMark";

/** Preview only: files stay local until the enclosing match form saves. */
export function OpponentLogoField({
  name,
  savedLogoUrl,
  file,
  onFile,
}: {
  name: string;
  savedLogoUrl: string | null;
  file: File | null;
  onFile: (file: File | null) => void;
}) {
  const inputId = useId();
  const inputRef = useRef<HTMLInputElement>(null);
  const [localUrl, setLocalUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!file) {
      setLocalUrl(null);
      if (inputRef.current) inputRef.current.value = "";
      return;
    }
    const url = URL.createObjectURL(file);
    setLocalUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  const logoUrl = file ? localUrl : savedLogoUrl;

  return (
    <div className="match-opponent-logo-field">
      <div className="match-opponent-logo-heading">
        <label htmlFor={inputId}>Opponent logo</label>
        <small id={`${inputId}-formats`}>Optional · PNG, JPG, SVG or WebP</small>
      </div>
      <input
        ref={inputRef}
        id={inputId}
        type="file"
        aria-describedby={`${inputId}-formats`}
        accept=".png,.jpg,.jpeg,.svg,.webp,image/png,image/jpeg,image/svg+xml,image/webp"
        onChange={(event) => onFile(event.currentTarget.files?.[0] ?? null)}
        data-testid="match-opponent-logo-input"
      />
      <div className="match-opponent-logo-selection">
        <div className="match-opponent-logo-preview" data-testid="match-opponent-logo-preview">
          <ClubMark
            name={name}
            imageUrl={logoUrl}
            initials={2}
            label={`${name} logo`}
            className="match-opponent-logo-mark"
          />
          <div className="match-opponent-logo-copy">
            <strong>{name}</strong>
            <span>{file ? file.name : savedLogoUrl ? "Current logo" : "Default initials crest"}</span>
          </div>
        </div>
        <button
          type="button"
          onClick={() => onFile(null)}
          className="match-opponent-logo-clear"
          disabled={!file}
        >
          Clear selection
        </button>
      </div>
    </div>
  );
}
